import { useUser } from "@dashboard/auth/useUser";
// 무통장입금 내역 (2026-08-10 KST 추가)
import { bankDepositsPath } from "@dashboard/bankDeposits/urls";
// 묶음상품 그룹 (2026-10-15 KST 추가)
import { bundleShippingGroupsPath } from "@dashboard/bundleShippingGroups/urls";
import { cashReceiptsPath } from "@dashboard/cashReceipts/urls";
import { categoryListUrl } from "@dashboard/categories/urls";
import { collectionListUrl } from "@dashboard/collections/urls";
import { navigationLucideIconProps } from "@dashboard/components/icons";
import { configurationMenuUrl } from "@dashboard/configuration/urls";
import { getConfigMenuItemsPermissions } from "@dashboard/configuration/utils";
import { rippleNewCustomersView } from "@dashboard/customers/ripples/newCustomersView";
import { customerGradesPath, customerListUrl } from "@dashboard/customers/urls";
// 디자인 관리 (2026-08-18 KST 추가)
import {
  type DesignSettingsPageNode,
  designSettingsPages,
  designSettingsPageUrl,
} from "@dashboard/designSettings/urls";
import { saleListUrl, voucherListUrl } from "@dashboard/discounts/urls";
import { SidebarAppAlert } from "@dashboard/extensions/components/AppAlerts/SidebarAppAlert";
import { useAppsAlert } from "@dashboard/extensions/components/AppAlerts/useAppsAlert";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import {
  extensionsAppSection,
  extensionsCustomSection,
  ExtensionsPaths,
  extensionsPluginSection,
} from "@dashboard/extensions/urls";
import { giftCardListUrl } from "@dashboard/giftCards/urls";
import { PermissionEnum } from "@dashboard/graphql";
import { ConfigurationIcon } from "@dashboard/icons/Configuration";
import { CustomersIcon } from "@dashboard/icons/Customers";
import { DiscountsIcon } from "@dashboard/icons/Discounts";
import { HomeIcon } from "@dashboard/icons/Home";
import { MarketplaceIcon } from "@dashboard/icons/Marketplace";
import { ModelingIcon } from "@dashboard/icons/Modeling";
import { OrdersIcon } from "@dashboard/icons/Orders";
import { ProductsIcon } from "@dashboard/icons/Products";
import { commonMessages, sectionNames } from "@dashboard/intl";
// 메시지(SMS/카카오 알림톡) 설정 (2026-08-10 KST 추가)
import { messageSettingsUrl } from "@dashboard/messageSettings/urls";
import { pageListPath } from "@dashboard/modeling/urls";
// 반품 관리 (2026-08-12 KST 추가)
import { orderReturnsPath } from "@dashboard/orderReturns/urls";
import { orderListUrl } from "@dashboard/orders/urls";
// 송장번호 일괄등록 (2026-10-29 KST 추가)
import { orderTrackingPath } from "@dashboard/orderTracking/urls";
import {
  productAddPath,
  productBasicInfoUrl,
  productListUrl,
  productPriceUrl,
  productTrashUrl,
} from "@dashboard/products/urls";
// 재입고(상품 판매) 알림 (2026-11-01 KST 추가)
import { restockAlarmsPath } from "@dashboard/restockAlarms/urls";
// 구매후기 목록 (2026-08-25 KST 추가)
import { reviewsPath } from "@dashboard/reviews/urls";
import { Ripple } from "@dashboard/ripples/components/Ripple";
// 매출 통계 (2026-09-05 KST 추가)
import { productSalesPath, salesStatsPath } from "@dashboard/salesStats/urls";
import { menuListUrl } from "@dashboard/structures/urls";
// 공급사 관리 (2026-08-26 KST 추가)
import { suppliersPath } from "@dashboard/suppliers/urls";
import { Box } from "@saleor/macaw-ui-next";
import { BarChart3, MessageSquare, Palette } from "lucide-react";
import { useIntl } from "react-intl";

import { SidebarIconSlot } from "../../SidebarIconSlot";
import { type SidebarMenuItem } from "../types";
import { mapToExtensionsItems } from "../utils";

export function useMenuStructure() {
  const { handleAppsListItemClick, hasProblems } = useAppsAlert();

  const extensions = useExtensions(extensionMountPoints.NAVIGATION_SIDEBAR);
  const intl = useIntl();
  const { user } = useUser();

  const appExtensionsHeaderItem: SidebarMenuItem = {
    id: "extensions",
    label: intl.formatMessage(sectionNames.appExtensions),
    type: "divider",
    paddingY: 1.5,
  };

  // 확장(Extensions) 메뉴를 감추면서 호출부만 주석 처리돼 미사용 상태다. 되살릴 때 주석을 지우면 된다.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getExtensionsSection = (): SidebarMenuItem => ({
    icon: renderIcon(<MarketplaceIcon />),
    label: intl.formatMessage(sectionNames.extensions),
    permissions: [],
    id: "installed-extensions",
    url: ExtensionsPaths.installedExtensions,
    type: "itemGroup",
    endAdornment: <SidebarAppAlert hasNewFailedAttempts={hasProblems} />,
    onClick: () => handleAppsListItemClick(new Date().toISOString()),
    children: [
      {
        label: (
          <Box display="flex" alignItems="center" gap={3}>
            {intl.formatMessage(sectionNames.installedExtensions)}
            <SidebarAppAlert hasNewFailedAttempts={hasProblems} small />
          </Box>
        ),
        id: "installed-extensions",
        url: ExtensionsPaths.installedExtensions,
        matchUrls: [
          ExtensionsPaths.installedExtensions,
          extensionsCustomSection,
          extensionsAppSection,
          extensionsPluginSection,
        ],
        permissions: [],
        type: "item",
      },
      {
        label: intl.formatMessage(sectionNames.exploreExtensions),
        id: "explore-extensions",
        url: ExtensionsPaths.exploreExtensions,
        permissions: [],
        type: "item" as const,
      },
    ],
  });

  const menuItems: SidebarMenuItem[] = [
    {
      icon: renderIcon(<HomeIcon />),
      label: intl.formatMessage(sectionNames.home),
      id: "home",
      url: "/",
      type: "item",
    },
    // '검색' 메뉴는 숨겼다. /search 페이지는 남아 있고, '/' 단축키도 이 메뉴의 배지가
    // 걸던 것이라 함께 빠진다 (2026-10-06 KST 수정)
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.products),
          id: "products",
          url: productListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        // 기본정보 관리 — 상품 기본정보만 모아 보는 목록 (2026-10-27 KST 추가)
        {
          label: intl.formatMessage({
            id: "AJ8aGw",
            defaultMessage: "기본정보 관리",
          }),
          id: "product-basic-info",
          url: productBasicInfoUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        // 가격 관리 — 공급가·판매가·이전가·부가세 구분 일괄 변경 (2026-10-27 KST 추가)
        {
          label: intl.formatMessage({
            id: "FESV5x",
            defaultMessage: "가격 관리",
          }),
          id: "product-price",
          url: productPriceUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        // 상품 등록 화면으로 바로 가는 메뉴 (2026-10-15 KST 추가)
        {
          label: intl.formatMessage({
            id: "RQXHPi",
            defaultMessage: "상품등록",
          }),
          id: "product-add",
          url: productAddPath,
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.categories),
          id: "categories",
          url: categoryListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.collections),
          id: "collections",
          url: collectionListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        // 상품 판매 알림 신청 — 신청 상품·신청자, 문자 발송. 컬렉션 아래 (2026-11-01 KST 추가)
        {
          label: intl.formatMessage({
            id: "nyUk3N",
            defaultMessage: "상품 판매 알림 신청",
          }),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          id: "restock-alarms",
          url: restockAlarmsPath,
          type: "item",
        },
        // 묶음상품 그룹 — 상품별 묶음배송 정책 (2026-10-15 KST 추가)
        {
          label: intl.formatMessage({
            id: "/+jyMC",
            defaultMessage: "묶음상품 그룹",
          }),
          id: "bundle-shipping-groups",
          url: bundleShippingGroupsPath,
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        // 기프트 카드는 할인 메뉴의 쿠폰 아래로 옮겼다 (2026-10-17 KST 수정)
        // 구매후기 — 상품별 후기 목록/베스트 지정/블라인드 (2026-08-25 KST 추가)
        {
          label: intl.formatMessage({
            id: "WLuBOj",
            defaultMessage: "구매후기",
          }),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          id: "reviews",
          url: reviewsPath,
          type: "item",
        },
        // 공급사 관리 — 상품 납품 업체 목록 (2026-08-26 KST 추가)
        {
          label: intl.formatMessage({
            id: "HN9xn5",
            defaultMessage: "공급사 관리",
          }),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          id: "suppliers",
          url: suppliersPath,
          type: "item",
        },
        // 삭제 보관함 — 삭제한 상품 목록과 [상품복원] (2026-10-14 KST 추가)
        {
          label: intl.formatMessage({
            id: "kRio5z",
            defaultMessage: "삭제 보관함",
          }),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          id: "product-trash",
          url: productTrashUrl(),
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_CATALOG, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<ProductsIcon />),
      url: productListUrl(),
      // 사이드바·상단 바 그룹명 '카탈로그' → '상품 관리' (2026-10-04 KST 수정)
      label: intl.formatMessage({
        id: "+1hSRG",
        defaultMessage: "상품 관리",
      }),
      permissions: [PermissionEnum.MANAGE_GIFT_CARD, PermissionEnum.MANAGE_PRODUCTS],
      id: "products",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.orders),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "orders",
          url: orderListUrl(),
          type: "item",
        },
        // 송장번호 일괄등록 — 파일로 택배사·송장번호 등록/변경 (2026-10-29 KST 추가)
        {
          label: intl.formatMessage({
            id: "2boucK",
            defaultMessage: "송장번호 일괄등록",
          }),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "order-tracking",
          url: orderTrackingPath,
          type: "item",
        },
        // '수동 주문'(임시 주문) 메뉴는 숨긴다. 주소로 들어가는 화면은 그대로다 (2026-10-07 KST 수정)
        // 현금영수증 관리 — 무통장입금 주문의 신청 목록/수동 발급 (2026-08-06 KST 추가)
        {
          label: intl.formatMessage({
            id: "h/AHkd",
            defaultMessage: "현금영수증",
          }),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "cash-receipts",
          url: cashReceiptsPath,
          type: "item",
        },
        // 무통장입금 내역 — 알뱅킹 콜백 입금내역과 주문 매칭 (2026-08-10 KST 추가)
        {
          label: intl.formatMessage({
            id: "bKPdYE",
            defaultMessage: "무통장입금 내역",
          }),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "bank-deposits",
          url: bankDepositsPath,
          type: "item",
        },
        // 반품 관리 — 스토어프론트 반품 신청 목록/반품완료 처리 (2026-08-12 KST 추가)
        {
          label: intl.formatMessage({
            id: "nhXwKd",
            defaultMessage: "반품 관리",
          }),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "order-returns",
          url: orderReturnsPath,
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_ORDERS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<OrdersIcon />),
      // 사이드바·상단 바 그룹명 '출고' → '주문 관리' (2026-10-04 KST 수정)
      label: intl.formatMessage({
        id: "ak8/kX",
        defaultMessage: "주문 관리",
      }),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "orders",
      url: orderListUrl(),
      type: "itemGroup",
    },
    // 매출/통계 — 결제확인일 기준 매출 집계 (2026-09-05 KST 추가)
    {
      children: [
        {
          label: intl.formatMessage({
            id: "EHLzRe",
            defaultMessage: "매출 통계",
          }),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "sales-stats",
          url: salesStatsPath,
          type: "item",
        },
        {
          label: intl.formatMessage({
            id: "53befk",
            defaultMessage: "상품별 매출",
          }),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "product-sales",
          url: productSalesPath,
          type: "item",
        },
      ],
      icon: renderIcon(<BarChart3 {...navigationLucideIconProps} />),
      label: intl.formatMessage({
        id: "p6NPYV",
        defaultMessage: "매출/통계",
      }),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "sales",
      url: salesStatsPath,
      type: "itemGroup",
    },
    // 회원 관리 아래에 '회원'(목록)과 '회원등급'을 둔다. 앱 확장 메뉴가 없어도 늘 그룹이다 (2026-10-08 KST 수정)
    {
      children: [
        {
          label: intl.formatMessage({
            defaultMessage: "회원",
            id: "RiXdyx",
          }),
          permissions: [
            PermissionEnum.MANAGE_USERS,
            PermissionEnum.MANAGE_ORDERS,
            PermissionEnum.MANAGE_STAFF,
          ],
          id: "customers",
          url: customerListUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage({
            defaultMessage: "회원등급",
            id: "3Hfifx",
          }),
          permissions: [PermissionEnum.MANAGE_USERS],
          id: "customer-grades",
          url: customerGradesPath,
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_CUSTOMERS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<CustomersIcon />),
      label: intl.formatMessage(sectionNames.customers),
      // Sidebar gating uses any-of matching, so users with only MANAGE_ORDERS
      // or MANAGE_STAFF can navigate to customer pages in read-only mode while
      // edit affordances remain hidden inside the section itself.
      permissions: [
        PermissionEnum.MANAGE_USERS,
        PermissionEnum.MANAGE_ORDERS,
        PermissionEnum.MANAGE_STAFF,
      ],
      endAdornment: <Ripple model={rippleNewCustomersView} />,
      id: "customers",
      url: customerListUrl(),
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.promotions),
          id: "promotions",
          url: saleListUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.vouchers),
          id: "vouchers",
          url: voucherListUrl(),
          type: "item",
        },
        // 기프트 카드 — 쿠폰 아래 (2026-10-17 KST 추가)
        {
          label: intl.formatMessage(sectionNames.giftCards),
          id: "giftCards",
          url: giftCardListUrl(),
          permissions: [PermissionEnum.MANAGE_GIFT_CARD],
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_DISCOUNTS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<DiscountsIcon />),
      label: intl.formatMessage(commonMessages.discounts),
      permissions: [PermissionEnum.MANAGE_DISCOUNTS],
      url: saleListUrl(),
      id: "discounts",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.models),
          id: "models",
          url: pageListPath,
          permissions: [PermissionEnum.MANAGE_PAGES],
          type: "item",
        },
        // '모델 유형'은 설정 페이지(모델 설정)에 있어 사이드 메뉴에서 뺀다 (2026-10-09 KST 수정)
        {
          label: intl.formatMessage(sectionNames.structures),
          id: "structures",
          url: menuListUrl(),
          permissions: [PermissionEnum.MANAGE_MENUS],
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_PAGES, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<ModelingIcon />),
      label: intl.formatMessage(sectionNames.modeling),
      permissions: [PermissionEnum.MANAGE_PAGES, PermissionEnum.MANAGE_MENUS],
      id: "modeling",
      url: pageListPath,
      type: "itemGroup",
    },
    // 디자인 관리 — 스토어프론트 외형 설정 (2026-08-18 KST 추가)
    {
      children: designSettingsPages.map(page => mapDesignSettingsPage(page)),
      icon: renderIcon(<Palette {...navigationLucideIconProps} />),
      label: "디자인 관리",
      permissions: [PermissionEnum.MANAGE_SETTINGS],
      id: "design",
      // 누르면 '기본 설정'으로 바로 간다. 활성 표시는 디자인 관리 전체에 걸지 않는다 (2026-10-01 KST 수정)
      url: designSettingsPageUrl("basic"),
      type: "itemGroup",
    },
    // 메시지(SMS/카카오 알림톡) — 설정 메뉴에서 사이드바로 이동 (2026-08-10 KST)
    {
      icon: renderIcon(<MessageSquare {...navigationLucideIconProps} />),
      label: intl.formatMessage({
        id: "HgXhKF",
        defaultMessage: "메시지",
      }),
      permissions: [PermissionEnum.MANAGE_SETTINGS],
      id: "message-settings",
      url: messageSettingsUrl,
      type: "item",
    },
    // {
    //   children: !isEmpty(extensions.NAVIGATION_TRANSLATIONS)
    //     ? [...mapToExtensionsItems(extensions.NAVIGATION_TRANSLATIONS, appExtensionsHeaderItem)]
    //     : undefined,
    //   icon: renderIcon(<TranslationsIcon />),
    //   label: intl.formatMessage(sectionNames.translations),
    //   permissions: [PermissionEnum.MANAGE_TRANSLATIONS],
    //   id: "translations",
    //   url: languageListUrl,
    //   type: !isEmpty(extensions.NAVIGATION_TRANSLATIONS) ? "itemGroup" : "item",
    // },
    // getExtensionsSection(),
    {
      icon: renderIcon(<ConfigurationIcon />),
      label: intl.formatMessage(sectionNames.configuration),
      permissions: getConfigMenuItemsPermissions(intl),
      id: "configure",
      url: configurationMenuUrl,
      type: "item",
    },
  ];
  const isMenuItemPermitted = (menuItem: SidebarMenuItem) => {
    const userPermissions = (user?.userPermissions || []).map(permission => permission.code);

    if (!menuItem?.permissions || menuItem?.permissions?.length < 1) {
      return true;
    }

    return menuItem.permissions.some(permission => userPermissions.includes(permission));
  };
  const getFilteredMenuItems = (menuItems: SidebarMenuItem[]) =>
    menuItems.filter(isMenuItemPermitted);

  return menuItems.reduce((resultItems: SidebarMenuItem[], menuItem: SidebarMenuItem) => {
    if (!isMenuItemPermitted(menuItem)) {
      return resultItems;
    }

    const { children } = menuItem;
    const filteredChildren = children ? getFilteredMenuItems(children) : undefined;

    return [...resultItems, { ...menuItem, children: filteredChildren }];
  }, []);
}

function renderIcon(icon: React.ReactNode) {
  return <SidebarIconSlot>{icon}</SidebarIconSlot>;
}

// 디자인 관리 화면 트리를 사이드바 항목으로 옮긴다 (2026-08-18 KST 추가)
// 하위 항목이 있으면 펼침 그룹이 되고, 그룹을 누르면 첫 하위 화면으로 이동한다.
// matchUrls 에 자기 경로를 넣어 어느 하위 화면에 있어도 그룹이 열린 상태로 유지된다.
function mapDesignSettingsPage(
  page: DesignSettingsPageNode,
  parentSegments: string[] = [],
): SidebarMenuItem {
  const segments = [...parentSegments, page.id];
  const common = {
    label: page.label,
    id: `design-${segments.join("-")}`,
    permissions: [PermissionEnum.MANAGE_SETTINGS],
  };

  if (!page.children) {
    return {
      ...common,
      url: designSettingsPageUrl(...segments),
      type: "item",
    };
  }

  return {
    ...common,
    url: designSettingsPageUrl(...segments, page.children[0].id),
    matchUrls: [designSettingsPageUrl(...segments)],
    type: "itemGroup",
    children: page.children.map(child => mapDesignSettingsPage(child, segments)),
  };
}
